import { IStringLocalizer } from "../../utilities/IStringLocalizer";
import { ReportTypeWithContext } from "../Models/Api/CustomerApiRequestsAndResponses";
import { DynamicRegulation } from "../Models/Compliance/DynamicRegulation";
import { CustomerRequest, ReportContext } from "../Models/Customers/Customer";
import { RegulationManager } from "./RegulationManager";

// ===============================================================================

/**
 * This manager handles the getting of the report types for a particular Exchange company. 
 * It also contains methods to converts report types to various forms as well as contains
 * the allowed context for each report type.
 */
export class ReportTypesManager {

    // ===============================================================================

    public static readonly standardReportTypes: Readonly<string[]> = [
        "compliance-letter",
        "reach-rohs-statement",
        "ipc",
        "hpd",
        "hpd-simple",
        "hpd-material-threshold",
        "leed",
        "pcds",
        "compliance-letter-lbc",
        "smelter-known-list",
        "smelter-unknown-list",
        "smelter-unique-known-list",
        "smelter-unique-unknown-list"
    ];

    // ===============================================================================

    public static readonly freeAccountReportTypes: Readonly<string[]> = [
        "compliance-letter",
        "reach-rohs-statement",
        "ipc"
    ];

    // ===============================================================================

    public static readonly allowedBulkReportTypes: Readonly<string[]> = [
        "compliance-letter",
        "reach-rohs-statement",
        "hpd",
        "hpd-simple",
        "hpd-material-threshold",
        "teknion-eds",
        "teknion-eds-french",
        "teknion-eds-2",
        "teknion-eds-2-french",
        "leed",
        "pcds",
        "leed-mecho",
        "hni-eds",
        "mapei-custom-report",
        "sgls-custom-report",
        "compliance-letter-lbc"
    ];

    // ===============================================================================

    private static readonly reportTypesThatDoNotNeedProductPercentages: Readonly<string[]> = [
        "smelter-known-list",
        "smelter-unknown-list",
        "smelter-unique-known-list",
        "smelter-unique-unknown-list"
    ];

    // ===============================================================================

    public static reportNeedsProductPercentages(reportType: string) {
        return !this.reportTypesThatDoNotNeedProductPercentages.includes(reportType);
    }

    // ===============================================================================

    /**
   * Given a list of report types, this method selects gets the possible contexts for each.
   * this would be called if a client needed to display all the regulations a certain report
   * type had.
   *
   * @param reportTypes the reports types to get the contexts from.
   */
    public static getReportTypesAndContextFromReportTypes(reportTypes: string[], reportTypesToRegulations: { [reportType: string]: string[] }) {
        const ret: { [reportType: string]: ReportContext | null } = {}

        for (let r of reportTypes) {
            let context: ReportContext | null = null;

            const regs = reportTypesToRegulations[r];

            if (regs) {
                context = {
                    regulations: regs,
                    listId: null
                };
            }

            ret[r] = context;
        }

        return ret;
    }

    // ===============================================================================

    /**
    * Given a report type context as a json string, this method parses that json string and returns
    * a report context object.
    *
    * @param contextJson the report type context json as a string
    */
    public static getReportContextFromReportContextJson(contextJson: string | null) {
        try {
            return contextJson === null ? null : JSON.parse(contextJson) as ReportContext;
        }
        catch {
            console.error("Failed to parse JSON: " + contextJson);
            throw new Error("Failed to parse JSON in constructReportTypesToRenderFromReportTypesWithContext");
        }
    }

    // ===============================================================================

    /**
     * Creates and returns the report types and report type contexts for the currently
     * selected report types.
     *
     * @param selectedReportTypes the report types and sub report types currently selected by a user
     */
    public getReportTypesWithContexts(selectedReportTypes: { [reportType: string]: ReportContext | null }): ReportTypeWithContext[] {
        const reportTypes = Object.keys(selectedReportTypes);

        return reportTypes.map(r => {
            const context = selectedReportTypes[r] === null ? null : JSON.stringify(selectedReportTypes[r]);

            return {
                reportType: r,
                contextJson: context
            } as ReportTypeWithContext;
        });
    }

    // ===============================================================================

    /**
     * Contains a map of all report types, both standard and customizable and maps them to a
     * user friendly name. An error will be thrown if a report type is given that does not have
     * a corresponding entry in the map.
     * 
     * @param reportTypeOrRegulation the report types to get the friendly name for
     * @param localizer user specific localizer for the friendly name
     */
    public static getFriendlyNameForReportTypeOrRegulation(reportTypeOrRegulation: string, localizer: IStringLocalizer, dynRegs: DynamicRegulation[]) {
        // TODO NF => instead of handling legacy regulation values here, we should deserialize all report contexts
        // in the backend and handle any legacy values there.
        const reportTypesToFriendlyNames: { [reportType: string]: string } = {
            "compliance-letter": localizer.localizeString("Regulatory Screening Letter"),
            "reach-rohs-statement": localizer.localizeString("Compliance Report"),
            "hpd": localizer.localizeString("HPD - Nested Material Inventory, Product Threshold"),
            "hpd-simple": localizer.localizeString("HPD - Basic Inventory (only substances)"),
            "hpd-material-threshold": localizer.localizeString("HPD - Nested Material Inventory, Material Thresholds"),
            "leed": localizer.localizeString("LEED"),
            "teknion-eds": localizer.localizeString("Teknion EDS"),
            "teknion-eds-french": localizer.localizeString("Teknion EDS (French)"),
            "teknion-eds-2": localizer.localizeString("Teknion TK EDS"),
            "teknion-eds-2-french": localizer.localizeString("Teknion TK EDS (French)"),
            "hni-eds": localizer.localizeString("HNI EDS"),
            "mapei-custom-report": localizer.localizeString("Mapei Custom Report"),
            "leed-mecho": localizer.localizeString("Mecho LEED"),
            "cal-65": localizer.localizeString("California Prop 65"),
            "reach": localizer.localizeString("EU REACH SVHC Candidate List & SCIP"),
            "rohs": localizer.localizeString("EU RoHS"),
            "conflict-minerals": localizer.localizeString("Conflict Minerals"),
            "coneg": localizer.localizeString("CONEG"),
            "eu-pops": localizer.localizeString("EU POPs"),
            "cmr": localizer.localizeString("EU MDR CMR-ED"),
            "generic": localizer.localizeString("Generic Statement"),
            "sgls-custom-report": localizer.localizeString("Saint-Gobain Custom Report"),
            "pcds": localizer.localizeString("Product Circularity Data Sheet (PCDS)"),
            "ipc": localizer.localizeString("IPC"),
            "compliance-letter-lbc": localizer.localizeString("LBC 4.0 Red List Letter"),
            "reach-authorization": localizer.localizeString("EU REACH Authorisation"),
            "scip": localizer.localizeString("EU REACH SCIP Database"),
            ...dynRegs.reduce((table: {[regulationId: string]: string}, r: DynamicRegulation) => {
                table[r.regulationId] = r.config.name;
                return table;
            }, {})
        };

        let ret = reportTypesToFriendlyNames[reportTypeOrRegulation];

        if (!ret) {
            console.error("Unknown report type " + reportTypeOrRegulation);
        }

        return ret ?? localizer.localizeString("Unknown");
    }

    // ===============================================================================

    /**
    * Returns true if the customer request requires a list to respond to a request.
    * Else returns false.
    * 
    * @param request Customer Request
    */
    public static doesCustomerRequestRequireList(request: CustomerRequest) {
        const reports = request.requestData.reportTypesAndContexts;

        for (let r of reports) {
            const context = this.getReportContextFromReportContextJson(r.contextJson);

            if (context !== null && (context.regulations.includes("generic") ||
                context.regulations.includes(RegulationManager.SaintGobainGenericRegulationId))) {
                return true;
            }
        }

        return false;
    }


    // ===============================================================================

    /**
    * Given a list of report types or regulations, this method returns them in alphabetical order
    * based on the friendly names for each report.
    * 
    * @param reportTypesOrRegulations Report Types or regualtions
    */

    public static getSortedListOfReportTypesOrRegulations(reportTypesOrRegulations: string[], localizer: IStringLocalizer, dynRegs: DynamicRegulation[]) {
        return reportTypesOrRegulations.sort((a: string, b: string) => {
            return ReportTypesManager.getFriendlyNameForReportTypeOrRegulation(a, localizer, dynRegs).localeCompare(ReportTypesManager.getFriendlyNameForReportTypeOrRegulation(b, localizer, dynRegs));
        });
    }
}
